/**
 * Settings > Outage Notifications: automatic customer outage emails from support@sonicnet.net.au.
 * Off by default. Test mode sends only to the staff addresses below and shows who would have been emailed.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import apiService from '../services/api';

type Mode = 'off' | 'test' | 'live';

interface Settings {
  mode: Mode;
  test_recipients: string[];
  send_created: boolean;
  send_planned: boolean;
  send_updates: boolean;
  send_resolved: boolean;
  service_types: string[];
  max_accounts_auto: number;
  active_since: string | null;
  available_service_types: { value: string; label: string }[];
}

const apiMessage = (e: any, fallback: string) => {
  const detail = e?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
};

const MODE_TEXT: Record<Mode, { title: string; body: string; severity: 'info' | 'warning' | 'success' }> = {
  off: { title: 'Off', body: 'No automatic customer notifications are sent. The "Email customers this update" tick on outage updates does nothing, and the manual Send Email button on an outage still works.', severity: 'info' },
  test: { title: 'Test mode', body: 'Nothing goes to customers. For each outage event the test recipients get one "[TEST]" email listing the accounts that would have been emailed, with a sample of the message.', severity: 'warning' },
  live: { title: 'Live', body: 'Customers are emailed automatically from support@sonicnet.net.au.', severity: 'success' },
};

const OutageNotificationsSettings: React.FC = () => {
  const [s, setS] = useState<Settings | null>(null);
  const [recipients, setRecipients] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sampling, setSampling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmLive, setConfirmLive] = useState(false);

  const load = useCallback(async () => {
    try {
      const data: Settings = await apiService.getOutageNotificationSettings();
      setS(data);
      setRecipients((data.test_recipients || []).join('\n'));
    } catch (e) {
      setError(apiMessage(e, 'Could not load outage notification settings.'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const payload = (settings: Settings) => ({
    enabled: settings.mode !== 'off',
    test_mode: settings.mode !== 'live',
    test_recipients: recipients.split(/[\s,;]+/).filter(Boolean),
    send_created: settings.send_created,
    send_planned: settings.send_planned,
    send_updates: settings.send_updates,
    send_resolved: settings.send_resolved,
    service_types: settings.service_types,
    max_accounts_auto: settings.max_accounts_auto,
  });

  const save = async () => {
    if (!s) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const saved: Settings = await apiService.saveOutageNotificationSettings(payload(s));
      setS(saved);
      setRecipients((saved.test_recipients || []).join('\n'));
      setNotice(`Saved. Outage notifications are ${MODE_TEXT[saved.mode].title.toLowerCase()}.`);
    } catch (e) {
      setError(apiMessage(e, 'Could not save.'));
    } finally {
      setSaving(false);
      setConfirmLive(false);
    }
  };

  const sendSample = async () => {
    setSampling(true);
    setError(null);
    setNotice(null);
    try {
      // Save first so the sample goes to the addresses currently typed in
      if (s) await apiService.saveOutageNotificationSettings(payload(s));
      const r = await apiService.sendOutageNotificationSample();
      setNotice(`Sample email sent to ${r.sent} of ${r.recipients} test recipient(s).`);
      await load();
    } catch (e) {
      setError(apiMessage(e, 'Could not send the sample.'));
    } finally {
      setSampling(false);
    }
  };

  if (loading) return <Box sx={{ p: 4 }}><CircularProgress /></Box>;
  if (!s) return <Alert severity="error">{error || 'Could not load settings.'}</Alert>;

  const toggleType = (value: string) =>
    setS({ ...s, service_types: s.service_types.includes(value) ? s.service_types.filter((t) => t !== value) : [...s.service_types, value] });
  const info = MODE_TEXT[s.mode];

  return (
    <Paper variant="outlined" sx={{ p: 3, maxWidth: 860 }}>
      <Typography variant="h6" gutterBottom>Outage notifications to customers</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Emails go out from <strong>support@sonicnet.net.au</strong> to the account email and any <strong>Technical</strong> contacts
        (one email per account, listing all of its affected services), only for accounts with Email notifications ticked.
        A new outage is notified a couple of minutes after it is created so the carrier feed can link customers first.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

      <Typography variant="subtitle2">Mode</Typography>
      <RadioGroup value={s.mode} onChange={(e) => setS({ ...s, mode: e.target.value as Mode })}>
        <FormControlLabel value="off" control={<Radio />} label="Off" />
        <FormControlLabel value="test" control={<Radio />} label="Test mode: only emails the test recipients below" />
        <FormControlLabel value="live" control={<Radio />} label="Live: emails customers" />
      </RadioGroup>
      <Alert severity={info.severity} sx={{ my: 1.5 }}>
        <strong>{info.title}.</strong> {info.body}
        {s.mode !== 'off' && s.active_since && <> Only outages created after {new Date(s.active_since).toLocaleString('en-AU')} are notified.</>}
      </Alert>

      <TextField
        label="Test recipients (staff)"
        multiline
        minRows={2}
        fullWidth
        value={recipients}
        onChange={(e) => setRecipients(e.target.value)}
        helperText="One email address per line. Required for test mode, and used for the sample email."
        sx={{ mb: 2 }}
      />

      <Typography variant="subtitle2" sx={{ mt: 1 }}>What to send</Typography>
      <FormGroup sx={{ mb: 2 }}>
        <FormControlLabel control={<Checkbox checked={s.send_created} onChange={(e) => setS({ ...s, send_created: e.target.checked })} />} label="New outage (unplanned)" />
        <FormControlLabel control={<Checkbox checked={s.send_planned} onChange={(e) => setS({ ...s, send_planned: e.target.checked })} />} label="Planned maintenance notice" />
        <FormControlLabel control={<Checkbox checked={s.send_updates} onChange={(e) => setS({ ...s, send_updates: e.target.checked })} />} label="Updates posted by staff (the tick on each update, ticked by default)" />
        <FormControlLabel control={<Checkbox checked={s.send_resolved} onChange={(e) => setS({ ...s, send_resolved: e.target.checked })} />} label="Resolved (only to customers who were told about the outage)" />
      </FormGroup>

      <Typography variant="subtitle2">Services covered</Typography>
      <FormGroup row sx={{ mb: 2 }}>
        {s.available_service_types.map((t) => (
          <FormControlLabel key={t.value} control={<Checkbox checked={s.service_types.includes(t.value)} onChange={() => toggleType(t.value)} />} label={t.label} />
        ))}
      </FormGroup>

      <TextField
        label="Safety limit (accounts)"
        type="number"
        size="small"
        value={s.max_accounts_auto}
        onChange={(e) => setS({ ...s, max_accounts_auto: Math.max(1, Number(e.target.value) || 1) })}
        helperText="An outage affecting more accounts than this is held (admins are told) so it can be reviewed and sent manually."
        sx={{ width: 260, mb: 3 }}
      />

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button variant="contained" disabled={saving} onClick={() => (s.mode === 'live' ? setConfirmLive(true) : save())}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button variant="outlined" disabled={sampling || !recipients.trim()} onClick={sendSample}>
          {sampling ? 'Sending…' : 'Send sample email to test recipients'}
        </Button>
      </Box>

      <Dialog open={confirmLive} onClose={() => setConfirmLive(false)}>
        <DialogTitle>Go live?</DialogTitle>
        <DialogContent>
          <Typography>
            Customers will be emailed automatically about new outages, planned maintenance, updates and resolutions. Only outages created
            from now on are notified. You can switch back to test mode or off at any time.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmLive(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={save} disabled={saving}>Go live</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default OutageNotificationsSettings;
